import { atom, selector, DefaultValue } from "recoil"
import { chatsAtom } from "../chats/index"
import { OverlayModel } from "./types"
import { badgesSelector } from "../badges"
import { pipParamsSelector } from "../pictureInPicture"
import { ConsoleLogger } from "../../../util/logger"
import { auctionParamsAtom } from "../auction"
import { productOverlaySelector } from "../product-overlay"
const logger = new ConsoleLogger()
const AuctionStatus = window.MeetOnline.AuctionStatus

export const overlayAtom = atom<OverlayModel>({
  key: "overlayAtom",
  default: null
})

export const stopPropagationMobileProductAtom = atom({
  key: "stopPropogationMobileProductAtom",
  default: true
})

export const currentVisibleOverlayAtom = atom<OverlayModel>({
  key: "currentVisibleOverlayAtom",
  default: null
})

export const isUserOnProceedToBuyScreenAtom = atom({
  key: "isUserOnProceedToBuyScreenAtom",
  default: false
})

export const overlaySelector = selector<OverlayModel | null>({
  key: "overlaySelector",
  get: ({ get }) => {
    return get(overlayAtom)
  },
  set: ({ set, get }, overlay) => {
    if (overlay instanceof DefaultValue) {
      return
    }
    const isUserOnProceedToBuyScreen = get(isUserOnProceedToBuyScreenAtom)

    // If overlay is null. then clear the data
    if (!overlay) {
      if (!isUserOnProceedToBuyScreen) {
        set(currentVisibleOverlayAtom, null)
      }
      set(overlayAtom, null)
      return
    }

    // if publish is false
    if (overlay.published === false && overlay.unpublishedId !== undefined) {
      const prevOverlay = get(overlayAtom)
      if (prevOverlay?.id === overlay.unpublishedId) {
        // if current overlay is unpublished
        if (!isUserOnProceedToBuyScreen) {
          set(currentVisibleOverlayAtom, null)
        } else {
          // If product is delete and user is on proceed to buy screen
          if (overlay.delete) {
            set(currentVisibleOverlayAtom, prev => {
              // current Visible overlay is delete. if yes, add the isProductDelete property
              if (prev.id === overlay.delete) {
                return {
                  ...prev,
                  delete: overlay.delete,
                  isProductDelete: true
                }
              }
              return prev
            })
          }
        }
        set(overlayAtom, null)
      }
      return
    }

    if (overlay !== null && overlay !== undefined) {
      const { id, chatStatus, parentChatId, type, published } =
        overlay as OverlayModel

      if (type === "badge") {
        if (get(badgesSelector(id)) !== undefined) {
          set(badgesSelector(id), badge => {
            return { ...badge, published: published }
          })
        }
      }

      if (chatStatus !== undefined) {
        if (get(chatsAtom(id))) {
          set(chatsAtom(id), chat => ({
            ...chat,
            published: published
          }))
        }
        if (parentChatId !== undefined && get(chatsAtom(parentChatId))) {
          set(chatsAtom(parentChatId), chat => {
            const replys =
              chat?.replyChats !== undefined
                ? chat.replyChats.map(reply => {
                    if (reply.id === id) {
                      return { ...reply, published: published }
                    }
                    return reply
                  })
                : chat?.replyChats === undefined
                ? []
                : chat.replyChats
            return { ...chat, replyChats: replys }
          })
        }
      }
    }

    if (overlay?.type === "chat" && overlay.message === undefined) {
      // no message to show
      return
    }

    if (!isUserOnProceedToBuyScreen) {
      // user is not on proceed to buy screen
      set(currentVisibleOverlayAtom, overlay)
    }
    // if user is on proceed to buy screen and prev-overlay match the current overlay and published is true
    const prevOverlay = get(overlayAtom)
    if (
      isUserOnProceedToBuyScreen &&
      prevOverlay?.id === overlay.id &&
      overlay.published
    ) {
      set(currentVisibleOverlayAtom, prev => ({ ...prev, ...overlay }))
    }

    set(overlayAtom, overlay)
  }
})

interface OverlayCallbackSelectorProps {
  handleReturnData?: () => Promise<OverlayModel | null>
  type?: "RESET"
  payload?: {
    overlay: OverlayModel
  }
}

export const overlayCallbackSelector = selector<OverlayCallbackSelectorProps>({
  key: "overlayCallbackSelector",
  get: ({ get, getCallback }) => {
    const handleReturnData = getCallback(({ snapshot }) => async () => {
      const data = await snapshot.getPromise(overlayAtom)

      return data
    })
    return { handleReturnData }
  },
  set: ({ set, reset }, action) => {
    if (action instanceof DefaultValue) {
      return false
    } else {
      const { type, payload } = action
      if (type === "RESET") {
        reset(overlayAtom)
      } else {
        set(overlayAtom, payload.overlay)
      }
    }
  }
})

export const showBadgeOverlayAtom = atom({
  key: "showBadgeOverlayAtom",
  default: true
})

export const userOnProceedToBuyScreenSelection = selector({
  key: "userOnProceedToBuyScreenSelection",
  get: ({ get }) => {
    return get(isUserOnProceedToBuyScreenAtom)
  },
  set: ({ get, set }, value) => {
    if (value) {
      set(isUserOnProceedToBuyScreenAtom, true)
    } else {
      set(isUserOnProceedToBuyScreenAtom, false)
      const latestOverlay = get(overlayAtom)
      set(currentVisibleOverlayAtom, latestOverlay)
      if (latestOverlay && latestOverlay.type === "badge") {
        set(pipParamsSelector, {
          coverImage: latestOverlay?.coverImages?.[0]?.src
            ? latestOverlay?.coverImages[0].src
            : ""
        })
      }

      if (
        !latestOverlay &&
        ((latestOverlay?.type === "badge" && !latestOverlay?.published) ||
          (latestOverlay?.type !== "badge" && latestOverlay?.published))
      ) {
        // Exit the PIP
        set(pipParamsSelector, { isActivePiP: false, coverImage: undefined })
        if (document.pictureInPictureElement) {
          try {
            document.exitPictureInPicture()
          } catch (err) {
            logger.error("error", err)
          }
        }
      }
    }
  }
})

export const enebledOverlaySelector = selector<boolean>({
  key: "enebledOverlaySelector",
  get: ({ get }) =>
    (get(overlayAtom) && get(showBadgeOverlayAtom)) ||
    get(productOverlaySelector).isOpen ||
    (get(auctionParamsAtom).isShowOverlay &&
      get(auctionParamsAtom).status === AuctionStatus.IN_PROGRESS)
})
